import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild,
  OnChanges
} from '@angular/core';
import { EnfafIconComponent } from '../enfaf-icon/enfaf-icon.component';
import { ToastService } from '../../core/services/toast/toast.service';
import { UtilsService } from '../../shared/utils/utils.service';
import { AlertService } from '../../core/services/alert/alert.service';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Subject, takeUntil } from 'rxjs';
import { FileType } from '../../models/enums/file_type.enum';
import { CustomPdfFile } from '../../models/enums/pdf_file.interface';
import { CustomFile, CustomUploadedFile } from '../../models/interfaces/custom_file.interface';
import { FileResult } from '../../models/interfaces/file_result.interface';
import { EnfafButtonComponent } from '../enfaf-button/enfaf-button.component';

@Component({
  selector: 'app-enfaf-input-file-dragger-dark',
  imports: [CommonModule, EnfafIconComponent, EnfafButtonComponent],
  templateUrl: './enfaf-input-file-dragger-dark.component.html',
  styleUrl: './enfaf-input-file-dragger-dark.component.scss'
})
export class EnfafInputFileDraggerDarkComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  @Input() accept: string = '*';
  @Input() maxFileSize: number = 250000000; // 250 MB
  @Input() acceptMultiple: boolean = false;
  @Input() acceptLimit: number = 10;
  @Input() cleanFiles: Subject<any> = new Subject();
  @Input() color: string = 'primary';

  @Input() title: string = 'Arrastra o suelta aquí';
  @Input() subtitle: string = 'o';
  @Input() buttonText: string = 'Sube tus archivos';
  @Input() noFilesText: string = 'Ningún archivo seleccionado';

  @Input() set currentFiles(value: CustomUploadedFile[] | undefined) {
    this._currentFiles = value || [];
    this.updateTotalFilesSize();
  }

  get currentFiles(): CustomUploadedFile[] {
    return this._currentFiles;
  }

  private _currentFiles: CustomUploadedFile[] = [];

  @Input() set localFiles(value: CustomFile[] | undefined) {
    if (value && value.length > 0) {
      this.files = value.map((file) => ({ ...file }));

      this.files.forEach((file) => {
        if (file.file && file.file instanceof File && file.file.size > 0) {
          const fileType = file.fileType || this.utils.getFileTypeByFileName(file.name);

          if (!file.fileType) {
            file.fileType = fileType;
          }

          if (fileType === FileType.Image && !file.blobUrl) {
            file.blobUrl = this.createAndCacheObjectURL(file.file);
          }
        } else {
          if (!file.fileType && file.name) {
            file.fileType = this.utils.getFileTypeByFileName(file.name);
          }
        }
      });
      this.updateTotalFilesSize();
    } else if (value !== undefined && value.length === 0) {
      this.files = [];
      this.updateTotalFilesSize();
    }
  }

  get localFiles(): CustomFile[] {
    return this.files;
  }

  @Output() filesDropped = new EventEmitter<CustomFile[]>();
  @Output() localFileDeleted = new EventEmitter<FileResult>();
  @Output() currentFileDeleted = new EventEmitter<CustomUploadedFile>();
  @Output() localFilesUpdated = new EventEmitter<CustomFile[]>();

  isUploadingFile = false;
  isDraggingOver = false;
  files: CustomFile[] = [];
  totalFilesSize = 0;
  private blobUrlCache: Map<string, string> = new Map();

  private unsubscribe$ = new Subject<void>();

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private toast: ToastService,
    private alert: AlertService,
    private sanitizer: DomSanitizer,
    private changeDetectorRef: ChangeDetectorRef,
    public utils: UtilsService
  ) {}

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    if (!this.accept) {
      this.accept = '*';
    }

    this.cleanFiles.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.files = [];
      this.revokeBlobUrls();
      this.blobUrlCache.clear();
      this.localFilesUpdated.emit([...this.files]);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes['acceptLimit'] && !changes['acceptLimit'].firstChange) {
      this.changeDetectorRef.detectChanges();
    }

    if (changes && changes['currentFiles'] && !changes['currentFiles'].firstChange) {
      this.updateTotalFilesSize();
    }

    if (changes && changes['localFiles'] && !changes['localFiles'].firstChange) {
      this.updateTotalFilesSize();
    }
  }

  updateTotalFilesSize(): void {
    let totalSize = 0;

    this.files.forEach((file) => {
      if (file.file && file.file.size) {
        totalSize += file.file.size;
      }
    });

    this.totalFilesSize = totalSize;
  }

  ngOnDestroy(): void {
    this.revokeBlobUrls();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private revokeBlobUrls(): void {
    this.blobUrlCache.forEach((url) => {
      URL.revokeObjectURL(url);
    });
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver = false;

    if (!event.dataTransfer?.files || !event.dataTransfer.files.length) {
      return;
    }

    const files = Array.from(event.dataTransfer.files);
    if (this.acceptLimit && files.length > this.acceptLimit) {
      const maxErrorText = `Máximo ${this.acceptLimit} archivos`;
      return this.toast.presentErrorToast(maxErrorText);
    }

    this.processFiles(files);
  }

  triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.processFiles(Array.from(input.files));
      input.value = '';
    }
  }

  async processFiles(fileList: File[]): Promise<void> {
    try {
      let errorMsgs = '';
      let receivedFiles: Array<any> = [];

      if (this.acceptMultiple) {
        for (let i = 0; i < fileList.length; i++) {
          if (this.files.length + receivedFiles.length >= this.acceptLimit) {
            break;
          }

          const file = fileList[i];
          const result = await this.processFile(file);
          if (result.errorMsg) {
            errorMsgs += result.errorMsg;
          } else if (result.file && result.fileType) {
            receivedFiles.push({
              name: file.name,
              file: result.file,
              fileType: result.fileType,
              previewSrc: result.previewSrc,
              blobUrl: result.fileType === FileType.Image ? this.createAndCacheObjectURL(result.file) : null
            });
          }
        }
        this.files.push(...receivedFiles);
      } else {
        const file = fileList[0];
        const result = await this.processFile(file);
        if (result.errorMsg) {
          errorMsgs += result.errorMsg;
        } else if (result.file && result.fileType) {
          receivedFiles = [
            {
              name: file.name,
              file: result.file,
              fileType: result.fileType,
              previewSrc: result.previewSrc,
              blobUrl: result.fileType === FileType.Image ? this.createAndCacheObjectURL(result.file) : null
            }
          ];

          if (this.files.length > 0) {
            this.files.forEach((oldFile) => {
              if (oldFile.blobUrl) {
                URL.revokeObjectURL(oldFile.blobUrl);
                this.blobUrlCache.delete(this.getFileKey(oldFile.file));
              }
            });
          }

          this.files = receivedFiles;
        }
      }

      if (this.utils.hasSomething(errorMsgs)) {
        this.toast.presentErrorToast(errorMsgs, 10000);
      }

      this.filesDropped.emit(this.files);
      this.localFilesUpdated.emit([...this.files]);
    } catch (error) {
      this.isUploadingFile = false;
      this.toast.presentErrorToast('Hubo un error al adjuntar los archivos');
    }
  }

  private createAndCacheObjectURL(file: File): string {
    const key = this.getFileKey(file);
    let url = this.blobUrlCache.get(key);

    if (!url) {
      url = URL.createObjectURL(file);
      this.blobUrlCache.set(key, url);
    }

    return url;
  }

  private getFileKey(file: File): string {
    return `${file.name}-${file.size}-${file.lastModified}`;
  }

  async processFile(file: File): Promise<{
    errorMsg?: string;
    file?: File;
    previewSrc?: string | SafeUrl | null;
    fileType?: string;
  }> {
    try {
      const fileName = file.name;
      let fileType: FileType;

      // Determine file type using MIME type or extension
      if (file.type.indexOf(FileType.Image) > -1) {
        fileType = FileType.Image;
      } else if (file.type.indexOf(FileType.Video) > -1) {
        fileType = FileType.Video;
      } else if (file.type.indexOf('pdf') > -1) {
        fileType = FileType.Document;
      } else if (file.type.indexOf('zip') > -1 || file.type.indexOf('compressed') > -1 || file.type.indexOf('x-rar') > -1) {
        fileType = FileType.Archive;
      } else {
        fileType = this.utils.getFileTypeByFileName(fileName);
      }

      if (this.accept !== '*') {
        const allowedTypes = this.accept.split(',').map((type) => type.trim());
        const fileExtension = file.name.split('.').pop()?.toLowerCase();

        if (
          !allowedTypes.some((type) => {
            if (type.startsWith('.')) {
              return fileExtension === type.substring(1);
            } else if (type.includes('/*')) {
              const mimeCategory = type.split('/')[0];
              return file.type.startsWith(mimeCategory);
            } else {
              return file.type === type || type === '*';
            }
          })
        ) {
          return { errorMsg: `- El archivo '${fileName}' debe ser de un formato válido (${this.accept})\n` };
        }
      }

      this.totalFilesSize += file.size;
      if (this.maxFileSize && this.totalFilesSize > this.maxFileSize) {
        this.totalFilesSize -= file.size;
        return { errorMsg: `- El peso total de los archivos no puede exceder los ${this.maxFileSize / this.utils.oneMegaByte} MB\n` };
      }

      if (fileType === FileType.Image && file.size > this.utils.maxImageSize) {
        return { errorMsg: `- La imagen '${fileName}' no puede exceder los ${this.utils.maxImageSize / this.utils.oneMegaByte} MB\n` };
      } else if (fileType === FileType.Video && file.size > this.utils.maxVideoSize) {
        return { errorMsg: `- El vídeo '${fileName}' no puede exceder los ${this.utils.maxVideoSize / this.utils.oneMegaByte} MB\n` };
      } else if (fileType === FileType.Document && file.size > this.utils.maxDocumentSize) {
        return { errorMsg: `- El documento '${fileName}' no puede exceder los ${this.utils.maxDocumentSize / this.utils.oneMegaByte} MB\n` };
      }

      if (file.size === 0) {
        return { errorMsg: `- El archivo '${fileName}' no puede estar vacío\n` };
      }

      let previewSrc: SafeUrl | string | null = null;
      if (fileType === FileType.Image) {
        const imgData = await this.utils.previewImageFile(file);
        const previewUnsafeStyle = `background-image: url(${imgData})`;
        const previewSafeStyle = this.sanitizer.bypassSecurityTrustStyle(previewUnsafeStyle);
        previewSrc = previewSafeStyle;
      } else if (fileType === FileType.Video) {
        previewSrc = this.utils.previewVideoFile(file);
      } else if (fileType === FileType.Document && file.type.indexOf('pdf') > -1) {
        const pdfData = await this.utils.previewImageFile(file);
        previewSrc = pdfData ? pdfData.replace('data:application/pdf;base64,', '') : null;
      }

      return { file, previewSrc, fileType };
    } catch (error) {
      return { errorMsg: `- Error al procesar el archivo\n` };
    }
  }

  async confirmRemoveFile($event: any, index: number): Promise<void> {
    $event.stopPropagation();
    $event.preventDefault();
    const header = 'Eliminar archivo';
    const message = '¿Estás seguro(a) de eliminar este archivo? Esta acción no se podrá deshacer.';
    const doNotDeleteFile = 'Cancelar';
    const deleteFile = 'Eliminar';
    const deleteConfirmed = await this.alert.presentConfirmActionAlert(header, message, doNotDeleteFile, deleteFile);
    if (deleteConfirmed) {
      this.removeFile(index);
    }
  }

  async confirmRemoveCurrentFile($event: any, file: CustomUploadedFile): Promise<void> {
    $event.stopPropagation();
    $event.preventDefault();
    this.currentFileDeleted.emit(file);
  }

  removeFile(index: number): void {
    if (index >= 0 && index < this.files.length) {
      const result: FileResult = {
        index: index,
        fileName: this.files[index].name || ''
      };

      const fileToRemove = this.files[index];
      if (fileToRemove.blobUrl) {
        URL.revokeObjectURL(fileToRemove.blobUrl);
        if (fileToRemove.file) {
          this.blobUrlCache.delete(this.getFileKey(fileToRemove.file));
        }
      }

      const removedFileSize = this.files[index].file.size;
      this.totalFilesSize -= removedFileSize;
      this.files.splice(index, 1);
      this.localFileDeleted.emit(result);
      this.filesDropped.emit(this.files);
      this.localFilesUpdated.emit([...this.files]);
    }
  }

  playVideo(url: SafeUrl): void {
    this.utils.playVideo(url);
  }

  previewDocument(url: any): void {
    this.utils.previewDocument(url, CustomPdfFile.Base64);
  }

  getImageSrc(file: CustomFile | CustomUploadedFile) {
    if ('file' in file) {
      // Prioridad 1: extraer URL de previewSrc para imágenes
      if (file.previewSrc && file.fileType === FileType.Image) {
        const extractedUrl = this.extractUrlFromPreviewSrc(file.previewSrc);
        if (extractedUrl) return extractedUrl;
      }

      // Prioridad 2: usar blobUrl existente
      if (file.file && file.blobUrl) {
        return file.blobUrl;
      }

      // Prioridad 3: crear blobUrl para archivos de imagen válidos
      if (file.file && file.file instanceof File && file.file.size > 0) {
        const fileIcon = this.utils.getFileIcon(file.name.split('.').pop() || '');

        if (file.fileType === FileType.Image || fileIcon === 'file-image') {
          file.blobUrl = this.createAndCacheObjectURL(file.file);
          return file.blobUrl;
        }
      }
    } else if ('path' in file && file.path) {
      const url = (file as any).presignedUrl || file.path;
      return url;
    }

    return '/assets/img/course-img-placeholder.png';
  }

  private extractUrlFromPreviewSrc(previewSrc: any): string | null {
    if (typeof previewSrc === 'object' && 'changingThisBreaksApplicationSecurity' in previewSrc) {
      const styleString = previewSrc.changingThisBreaksApplicationSecurity;
      const urlMatch = styleString.match(/url\(([^)]+)\)/);
      return urlMatch?.[1] || null;
    }
    return null;
  }

  private getImageUrlForPreview(file: CustomFile): string | null {
    // Prioridad 1: extraer URL de previewSrc
    if (file.previewSrc) {
      const extractedUrl = this.extractUrlFromPreviewSrc(file.previewSrc);
      if (extractedUrl) return extractedUrl;
    }

    // Prioridad 2: usar blobUrl existente
    if (file.blobUrl) {
      return file.blobUrl;
    }

    // Prioridad 3: crear nueva URL si hay file válido
    if (file.file && file.file instanceof File && file.file.size > 0) {
      return URL.createObjectURL(file.file);
    }

    return null;
  }

  async previewFile(file: CustomFile) {
    if (file.fileType === FileType.Video && file.previewSrc) {
      this.playVideo(file.previewSrc);
    } else if (file.fileType === FileType.Document && file.previewSrc) {
      this.previewDocument(file.previewSrc);
    } else if (file.fileType === FileType.Image) {
      const imageUrl = this.getImageUrlForPreview(file);

      if (imageUrl) {
        this.utils.previewImage(imageUrl);
      } else {
        this.toast.presentErrorToast('No se puede previsualizar esta imagen');
      }
    } else if (file.fileType === FileType.Archive && file.file && file.file instanceof File) {
      const blobUrl = URL.createObjectURL(file.file);
      this.utils.previewArchive(file.name, blobUrl, file.file.size, true);
    } else {
      this.toast.presentErrorToast('No se puede previsualizar este tipo de archivo');
    }
  }

  async previewCurrentFile(file: CustomUploadedFile) {
    if (!file || !(file.path || (file as any).presignedUrl)) {
      this.toast.presentErrorToast('No se puede previsualizar este archivo');
      return;
    }

    const fileType = this.utils.getFileTypeByFileName(file.filename);
    const fileUrl = (file as any).presignedUrl || file.path;
    const ext = file.filename.split('.').pop()?.toLowerCase() ?? '';

    try {
      if (fileType === FileType.Video) {
        this.utils.playVideo(fileUrl);
      } else if (fileType === FileType.Document && ext === 'pdf') {
        this.utils.previewDocument(fileUrl, CustomPdfFile.Remote);
      } else if (fileType === FileType.Image) {
        this.utils.previewImage(fileUrl);
      } else if (fileType === FileType.Archive) {
        this.utils.previewArchive(file.filename, fileUrl, undefined, false);
      } else {
        window.open(fileUrl, '_blank');
      }
    } catch (error) {
      this.toast.presentErrorToast('Error al previsualizar el archivo');
    }
  }
}
